import { useCallback, useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useTabDrafts } from "@/components/ui/box-draft";
import { classPlanExists } from "@/components/courses/use-unit-draft";
import type { Blocker, Effect, Part, Result, RowTask } from "@/components/ui/box.schema";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import type { RegisterPlanState, RegisterSession } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { courseUnitsApi, type CourseUnit, type UnitOperation } from "@/lib/api/courseUnits";
import { classDay, classTime } from "@/lib/class-plan-name";

import { planFailureWords } from "../session-plan-content";
import { PLACE_COPY } from "./session-copy";

const copy = PLACE_COPY;
const imam = { system: "imam" } as const;

/** The plan a row puts on a session: on none yet (C08), or on a cancelled one (C07's Move plan). */
export interface PlacedPlan {
  id: string;
  topic: string;
  state: RegisterPlanState;
  /** The plan's version as the register read it: the choice is bound to it (R4). */
  version: string;
  /** The cancelled session it is on, when it is moved from one. */
  from?: RegisterSession;
}

/** The session chosen, kept in this tab (O7, CASE-038). */
interface PlaceDraft {
  sessionId: string;
}

/** A send kept with its draft until its answer: its key, what it carried and what it said it would do (W1). */
interface PlaceSend {
  key: string;
  sent: PlaceDraft;
  target: string;
  effects: string[];
}

const NO_CHOICE: PlaceDraft = { sessionId: "" };

/** A session as a placement choice: "Tue, Sep 29, 6:00 PM · Opening". */
export function placeName(session: Pick<RegisterSession, "label" | "starts_at">, timezone: string): string {
  const words = session.label?.trim();
  if (!session.starts_at) return words || copy.unscheduled;
  const when = `${classDay(session.starts_at, timezone)}, ${classTime(session.starts_at, timezone)}`;
  return words ? `${when} · ${words}` : when;
}

/**
 * Putting a plan on a session, as its row's Task (C08, K3): the session is
 * its one part, and the commit sends it once under its key with the plan's
 * version (W1, R4). One session holds one plan, so a cancelled session or one
 * that holds a plan is never offered, and a choice the register has since
 * filled blocks the commit with its reason (F5, O8). A refusal keeps the
 * choice and says why; an answer that never came offers Check, which reads
 * the plan's own receipt and never sends again.
 */
export function usePlanPlacementTasks({ courseId, timezone, sessions, onChanged }: {
  courseId: string;
  timezone: string;
  /** The register's sessions, with the plan each holds (R1). */
  sessions: RegisterSession[];
  /** Reads the register and the pages a placement moves; awaited before the result is drawn. */
  onChanged: () => Promise<unknown>;
}): (plan: PlacedPlan, rowKey: string) => RowTask {
  const queryClient = useQueryClient();
  const ofDraft = useTabDrafts<PlaceDraft, PlaceSend>("plan-placement");
  const [results, setResults] = useState<Readonly<Record<string, Result>>>({});
  const id = useId();

  return useCallback((plan: PlacedPlan, rowKey: string): RowTask => {
    const kept = ofDraft({ record: plan.id, version: plan.version, initial: NO_CHOICE });
    const chosen = kept.value.sessionId;
    const settle = (next: Result) => {
      setResults((current) => ({ ...current, [plan.id]: next }));
      return next;
    };
    const part = `${rowKey}:session`;
    const field = `${id}-${plan.id}-session`;
    const offered = sessions.filter((session) => session.status !== "cancelled" && !session.plan);
    const target = sessions.find((session) => session.id === chosen);
    const name = target ? placeName(target, timezone) : null;
    const moving = Boolean(plan.from);
    const notDone = moving ? copy.notMoved : copy.notPlaced;

    const parts: Part[] = [{
      id: part,
      label: copy.session,
      state: name ? { class: "draft", word: name } : { class: "waiting", word: copy.noneChosen },
      owner: imam,
      fold: "never",
      draft: "browser",
      keep: kept,
      // The plan changed after the choice was made: compare, then keep the choice or start again (R4).
      conflict: kept.conflict ? {
        sentence: copy.changedSince,
        facts: [{ label: copy.planNow, theirs: plan.topic }],
        keepMine: { label: copy.keepChoice, run: () => { kept.keepMine(); settle({ state: "none" }); } },
        loadNewer: { label: copy.startAgain, run: () => { kept.loadNewer(); settle({ state: "none" }); } },
      } : undefined,
      render: () => (
        <div className="grid gap-1.5">
          <Label htmlFor={field}>{copy.session}</Label>
          <NativeSelect id={field} value={chosen} aria-describedby={`${field}-help`} onChange={(event) => kept.set({ sessionId: event.target.value })}>
            <option value="">{copy.choose}</option>
            {target && !offered.includes(target) && <option value={target.id}>{name}</option>}
            {offered.map((session) => <option key={session.id} value={session.id}>{placeName(session, timezone)}</option>)}
          </NativeSelect>
          <p id={`${field}-help`} className="text-meta text-muted-foreground">{copy.help}</p>
        </div>
      ),
    }];

    const effects = (): Effect[] => [
      { id: "places", sentence: copy.places(plan.topic, name ?? copy.chosenSession), part },
      ...(plan.from ? [{ id: "leaves", sentence: copy.leaves(placeName(plan.from, timezone)) }] : []),
      { id: "learners", sentence: plan.state === "draft" ? copy.unpublished : copy.published },
    ];
    const blockers = (): Blocker[] => {
      if (!chosen) return [{ kind: "missing", part, sentence: copy.chooseMissing }];
      // The register read again since the choice: that session now holds a plan, or no longer meets.
      if (!target || target.status === "cancelled") return [{ kind: "missing", part, sentence: copy.gone }];
      if (target.plan && target.plan.id !== plan.id) return [{ kind: "missing", part, sentence: copy.taken }];
      return [];
    };
    // What a confirmed placement did, from the send's own record, never from a choice made since.
    const confirmed = (unit: CourseUnit, send: PlaceSend): Result => ({
      state: "confirmed",
      cap: copy.done(send.target),
      at: unit.updated_at,
      effects: send.effects,
      remains: [],
      version: String(unit.revision),
    });
    const receipt = async (operation: UnitOperation, send: PlaceSend): Promise<Result> => {
      if (operation.state === "committed") {
        queryClient.setQueryData<CourseUnit[]>(["course-units", courseId], (current) => current?.map((unit) => (unit.id === operation.unit.id ? operation.unit : unit)));
        kept.setPending(null);
        if (kept.value.sessionId === send.sent.sessionId) kept.end();
        await onChanged();
        return confirmed(operation.unit, send);
      }
      if (operation.state === "not_committed") {
        // A definite no: said, with the choice kept for another send.
        kept.setPending(null);
        return { state: "refused", cap: notDone, sentence: copy.notCommitted, draft: "kept" };
      }
      return unknown(send);
    };
    const unknown = (send: PlaceSend): Result => ({
      state: "unknown",
      cap: copy.unknownCap,
      requestId: send.key,
      sentence: copy.unknown,
      check: {
        verb: copy.check,
        note: copy.checkNote,
        run: async () => {
          try {
            return settle(await receipt(await courseUnitsApi.reconcile(courseId, plan.id, send.key, "save"), send));
          } catch (error) {
            if (error instanceof ApiError && error.status < 500) {
              kept.setPending(null);
              return settle({ state: "refused", cap: notDone, sentence: planFailureWords(error.message), draft: "kept" });
            }
            return settle(unknown(send));
          }
        },
      },
    });

    return {
      parts,
      commit: {
        verb: moving ? copy.verbMove : copy.verb,
        cap: moving ? copy.capMove : copy.cap,
        effects,
        blockers,
        run: async (requestId) => {
          let next: Result;
          try {
            // The plan's version, from the plan list its editor and this row read (R4).
            const units = await queryClient.ensureQueryData({ queryKey: ["course-units", courseId], queryFn: () => courseUnitsApi.list(courseId) });
            const unit = units.find((candidate) => candidate.id === plan.id);
            if (!unit) {
              await onChanged();
              next = { state: "refused", cap: notDone, sentence: copy.missing, draft: "kept" };
            } else if (kept.began && Date.parse(unit.updated_at) !== Date.parse(kept.began)) {
              // The plan this choice began from is not the one read now: read both again and compare, never send it on the newer version (R4).
              await queryClient.invalidateQueries({ queryKey: ["course-units", courseId] });
              await onChanged();
              next = { state: "refused", cap: notDone, sentence: copy.changedSince, draft: "kept" };
            } else {
              const send: PlaceSend = { key: requestId, sent: kept.value, target: name ?? copy.chosenSession, effects: effects().map((effect) => effect.sentence) };
              kept.setPending(send);
              try {
                next = await receipt(await courseUnitsApi.patch(courseId, plan.id, { operation_id: requestId, revision: unit.revision, session_id: chosen }), send);
              } catch (error) {
                if (!(error instanceof ApiError) || error.status >= 500) next = unknown(send);
                else {
                  kept.setPending(null);
                  // A newer plan or a session filled first: read both again, so the comparison is shown before any other send (R4).
                  if (error.status === 409) {
                    await queryClient.invalidateQueries({ queryKey: ["course-units", courseId] });
                    void onChanged();
                  }
                  next = { state: "refused", cap: notDone, sentence: classPlanExists(error) ? copy.taken : planFailureWords(error.message), draft: "kept" };
                }
              }
            }
          } catch {
            // The plan list itself couldn't be read: nothing was sent.
            next = { state: "refused", cap: notDone, sentence: copy.unread, draft: "kept" };
          }
          return settle(next);
        },
      },
      // After a reload, a send still unanswered comes back as Not confirmed with Check on its own key (W1).
      result: results[plan.id] ?? (kept.pending ? unknown(kept.pending) : { state: "none" }),
    };
  }, [courseId, timezone, sessions, onChanged, ofDraft, results, id, queryClient]);
}
